// Facility grouping and one-line effects for the Buildings tab (UI polish spec §3.3).
import type { FacilityType, FullSystem } from '@stars-and-ships/api-types';
import { formatNumber } from './labels';

export interface FacilityGroup {
  id: 'core' | 'production' | 'storage' | 'military' | 'infrastructure';
  label: string;
  facilities: FacilityType[];
}

/** The slice facilities (S-1) in the order the Buildings tab shows them. */
export const FACILITY_GROUPS: readonly FacilityGroup[] = [
  { id: 'core', label: 'Core', facilities: ['command_center'] },
  {
    id: 'production',
    label: 'Production',
    facilities: ['metal_mine', 'gas_harvester', 'supply_farm'],
  },
  {
    id: 'storage',
    label: 'Storage',
    facilities: ['metal_storage', 'gas_storage', 'supply_storage'],
  },
  { id: 'military', label: 'Military', facilities: ['space_dock', 'habitat', 'odp'] },
  {
    id: 'infrastructure',
    label: 'Infrastructure',
    facilities: ['trading_port', 'warp_gate', 'diplomatic_embassy'],
  },
];

/** What the facility gives this system now, from data the system view already has; null if nothing short fits. */
export function facilityEffect(facility: FacilityType, system: FullSystem): string | null {
  const { ratesPerHour, caps } = system.resources;
  switch (facility) {
    case 'metal_mine':
      return `+${formatNumber(ratesPerHour.metal)}/h`;
    case 'gas_harvester':
      return `+${formatNumber(ratesPerHour.gas)}/h`;
    case 'supply_farm':
      return `+${formatNumber(ratesPerHour.supplies)}/h`;
    case 'metal_storage':
      return `cap ${formatNumber(caps.metal)}`;
    case 'gas_storage':
      return `cap ${formatNumber(caps.gas)}`;
    case 'supply_storage':
      return `cap ${formatNumber(caps.supplies)}`;
    case 'habitat':
      return `${formatNumber(system.habitat.capacity)} Habitat`;
    case 'diplomatic_embassy':
      return `+${formatNumber(system.influence.perHour)} Influence/h`;
    case 'trading_port':
      return system.tradingPort.level > 0
        ? `${formatNumber(system.tradingPort.creditsPer1000)} Credits per 1,000`
        : null;
    default:
      return null;
  }
}
